import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

// types
import { ServiceNames } from "../types/services.types"
import { withPrefix } from "gatsby"
import { extractLangCode, LANG_CODES_TO_TLD } from "../utils/general_utils"

export type HelpArticleSearchDocument = {
  articleid?: string
  helparticletitle?: string
  slug?: string
  articleaudience?: string
  summary?: string
  id?: string
  langcode?: string
  contentful_id?: string
  score?: string
  helparticlecategory?: string
  helparticlesubcategory?: string
  _score?: number
}

export type GetHelpArticlesByTitleArguments = {
  query: string
  propertiesToReturn?: Array<keyof HelpArticleSearchDocument>
}

export type SingleHit = {
  fields: HelpArticleSearchDocument
  id: string
}

export type ApiResponse = {
  result: {
    found: number
    hit: SingleHit[]
  }
}

type LANG = keyof typeof LANG_CODES_TO_TLD

const EN_LOCALES: LANG[] = [
  "en-au",
  "en-ca",
  "en-gb",
  "en-hk",
  "en-ie",
  "en-nz",
  "en-sg",
  "da",
  "fi",
  "sv"
]

// @TODO: need to allow for multiple lang codes e.g. ["de-ch", "de"]
const fetchNodeLocaleForSearch = () => {
  const node_locale = extractLangCode()?.toLowerCase() as LANG

  if (
    node_locale === "es-cl" ||
    node_locale === "es-co" ||
    node_locale === "es-pe"
  ) {
    return "es"
  }

  if (node_locale === "nl-nl") {
    return "nl"
  }

  if (node_locale === "fr-ca" || node_locale === "fr-ch") {
    return "fr"
  }

  if (node_locale === "de-ch") {
    return "de"
  }

  if (EN_LOCALES?.includes(node_locale)) {
    return "en-us"
  }

  return node_locale
}

// Define a service using a base URL and expected endpoints
export const helpArticlesApi = createApi({
  reducerPath: ServiceNames.helpArticle,
  baseQuery: fetchBaseQuery({
    baseUrl: withPrefix("/api/help-article"),
    timeout: 20000
  }),
  endpoints: (builder) => ({
    getHelpArticlesByTitle: builder.query<
      ApiResponse,
      GetHelpArticlesByTitleArguments
    >({
      query: ({ query, propertiesToReturn }) => {
        const finalLangCode = fetchNodeLocaleForSearch()

        const final = `?q=${encodeURIComponent(
          query
        )}&langCode=${finalLangCode}`

        return final
      }
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetHelpArticlesByTitleQuery,
  useLazyGetHelpArticlesByTitleQuery,
  endpoints
} = helpArticlesApi
